import React, { useEffect, useState } from 'react'
import { useMailCount } from '../common/hooks/useMailCount'
import {
  INITIAL_MAIL_COUNT,
  formatMailCount,
} from '../common/utils/initialMailCount'
import classNames from 'classnames'

interface MailCountTrustProps {
  className?: string
  highlightClassName?: string
  showAnimation?: boolean
}

/**
 * Display trust message with live mail count
 * "Thousands of companies have trusted us with X pieces of mail and counting"
 */
export const MailCountTrust: React.FC<MailCountTrustProps> = ({
  className,
  highlightClassName = 'color-pink2',
  showAnimation = true,
}) => {
  // Start with the build-time initial count for instant display
  const [displayCount, setDisplayCount] = useState<number>(INITIAL_MAIL_COUNT)
  const [prevCount, setPrevCount] = useState<number>(INITIAL_MAIL_COUNT)
  const [isUpdating, setIsUpdating] = useState(false)

  const { count, loading, error } = useMailCount()

  // Update display count when API returns fresh data
  useEffect(() => {
    if (count !== null && count !== displayCount) {
      setDisplayCount(count)
      setIsUpdating(true)
      setPrevCount(count)

      const timeout = setTimeout(() => {
        setIsUpdating(false)
      }, 600)

      return () => clearTimeout(timeout)
    }
  }, [count, displayCount])

  // Never show loading state - we always have a number to display
  // The initial count ensures immediate render with no skeleton

  return (
    <span className={classNames('whitespace-nowrap', className)}>
      Thousands of companies <br className='d-none d-lg-block' /> have trusted
      us with <br className='d-none d-lg-block' />
      <span
        className={classNames(highlightClassName, {
          'mail-count-updating': showAnimation && isUpdating,
        })}
        style={{
          transition: showAnimation ? 'all 0.3s ease' : undefined,
          display: 'inline-block',
          transform: isUpdating ? 'scale(1.02)' : 'scale(1)',
        }}
      >
        {formatMailCount(displayCount)} pieces of mail
      </span>{' '}
      and counting.
      {showAnimation && (
        <style jsx>{`
          @keyframes subtle-pulse {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.8;
            }
            100% {
              opacity: 1;
            }
          }

          .mail-count-updating {
            animation: subtle-pulse 0.6s ease;
          }
        `}</style>
      )}
    </span>
  )
}

/**
 * Compact version for smaller spaces
 */
export const MailCountTrustCompact: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { count, loading } = useMailCount()

  if (loading || count === null) {
    return (
      <span className={className}>Over 1 million pieces of mail handled</span>
    )
  }

  return (
    <span className={className}>
      Over {count.toLocaleString('en-GB')} pieces of mail handled
    </span>
  )
}
